import { validExitJob } from './txModel';
import { validDirectorJob } from './directorModel';
const validJob = job => validExitJob(job) || validDirectorJob(job);
export function createExitJournal(getIndexedDB = () => globalThis.indexedDB, name = 'pcd.ptt-exit.v1') {
  let opening;
  function open() {
    if (!opening) opening = new Promise((resolve, reject) => {
      if (!getIndexedDB()) return reject(Error('交易儲存不可用，請先恢復儲存權限。'));
      const request = getIndexedDB().open(name, 1);
      request.onupgradeneeded = () => request.result.createObjectStore('journal');
      request.onerror = () => reject(request.error);
      request.onblocked = () => reject(Error('交易儲存被其他頁面阻擋。'));
      request.onsuccess = () => { request.result.onversionchange = () => { request.result.close(); opening = null; }; resolve(request.result); };
    }).catch(error => { opening = null; throw error; });
    return opening;
  }
  // Resolve only on transaction completion: request success is not durable commit.
  async function transact(change) {
    const db = await open();
    return new Promise((resolve, reject) => {
      const tx = db.transaction('journal', 'readwrite'), store = tx.objectStore('journal');
      let value, failure;
      tx.oncomplete = () => failure ? reject(failure) : resolve(value);
      tx.onerror = tx.onabort = () => reject(failure || tx.error || Error('交易儲存未完成。'));
      const active = store.get('active');
      active.onerror = () => tx.abort();
      active.onsuccess = () => {
        try {
          if (active.result && !validJob(active.result)) throw Error('交易記錄無法核實，請勿重送。');
          value = change(store, active.result || null);
        } catch (error) { failure = error; tx.abort(); }
      };
    });
  }
  return {
    async snapshot() {
      const db = await open();
      return new Promise((resolve, reject) => {
        const tx = db.transaction('journal', 'readonly'), store = tx.objectStore('journal'), result = {};
        for (const [key, field] of [['active','active'],['last','last'],['last-actions.v2','actionLast']]) {
          const request = store.get(key); request.onsuccess = () => { result[field] = request.result || null; };
        }
        tx.onerror = tx.onabort = () => reject(tx.error || Error('交易儲存未完成。'));
        tx.oncomplete = () => {
          if ((result.active && !validJob(result.active)) || (result.actionLast && !validDirectorJob(result.actionLast))) return reject(Error('交易記錄無法核實，請勿重送。'));
          resolve(result);
        };
      });
    },
    inspect: () => transact((store, active) => active),
    acquire: job => transact((store, active) => { if (active) throw Error('已有舊版退出交易待處理，請先查詢。'); if (!validJob(job) || job.stage !== 'preparing') throw Error('Invalid B1 intent'); store.put(job, 'active'); return job; }),
    update: (id, change) => transact((store, active) => {
      if (!active || active.id !== id) return null;
      if (Object.keys(change).some(key => !['stage', 'hash', 'originalHash', 'nonce', 'replaced', 'requestBlock', 'requestNonce', 'candidateHash'].includes(key)) || (active.originalHash && change.originalHash && active.originalHash !== change.originalHash)) throw Error('Immutable B1 intent');
      const transitions = { preparing: ['preparing', 'wallet-requested'], 'wallet-requested': ['wallet-requested', 'submitted', 'submission-unknown'], 'submission-unknown': ['submission-unknown', 'submitted'], submitted: ['submitted', 'confirmed-unverified'], 'confirmed-unverified': ['confirmed-unverified'] };
      for (const key of ['requestBlock', 'requestNonce']) if (active[key] !== undefined && change[key] !== undefined && active[key] !== change[key]) throw Error('Immutable request context');
      if (change.stage && !transitions[active.stage].includes(change.stage)) throw Error('Invalid B1 transition');
      const next = { ...active, ...change, id: active.id };
      if (!validJob(next)) throw Error('Invalid B1 update');
      store.put(next, 'active'); return next;
    }),
    finish: (id, outcome, onlyPrepared = false, result = {}) => transact((store, active) => {
      if (!active || active.id !== id || (onlyPrepared && active.stage !== 'preparing')) return false;
      if (Object.keys(result).some(key => key !== 'paid') || (result.paid !== undefined && (active.version !== 2 || !/^\d{1,78}$/.test(result.paid) || BigInt(result.paid) <= 0n))) throw Error('Invalid action result');
      store.put({ ...active, ...result, outcome, finishedAt: Date.now() }, active.version === 2 ? 'last-actions.v2' : 'last'); store.delete('active'); return true;
    }),
    async lastActions() { const db = await open(); return new Promise((resolve, reject) => { const tx = db.transaction('journal', 'readonly'); const req = tx.objectStore('journal').get('last-actions.v2'); let value; req.onsuccess = () => { value = req.result; }; tx.oncomplete = () => value && !validDirectorJob(value) ? reject(Error('Invalid action history')) : resolve(value || null); tx.onerror = tx.onabort = () => reject(tx.error); }); },
    async last() { const db = await open(); return new Promise((resolve, reject) => { const tx = db.transaction('journal', 'readonly'); const req = tx.objectStore('journal').get('last'); let value; req.onsuccess = () => { value = req.result; }; tx.oncomplete = () => resolve(value || null); tx.onerror = tx.onabort = () => reject(tx.error); }); },
  };
}
export const exitJournal = createExitJournal();
